import { SyncWarningBadge } from '@/components/shared/SyncStatusBadge';
import { VoiceoverAudioPlayer } from '@/components/shared/VoiceoverAudioPlayer';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Languages } from 'lucide-react';
import { z } from 'zod';

/**
 * Zod schema mirroring server-side updateTourTranslationSchema in tours.schemas.ts.
 * Tour translations are only ever edited (generated server-side), so only an update schema is needed.
 */
const updateTourTranslationSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(255, 'Name cannot exceed 255 characters').optional(),
  description: z.string().optional().nullable(),
  tagline: z.string().max(500, 'Tagline cannot exceed 500 characters').optional().nullable(),
  conclusion: z.string().optional().nullable(),
});

export interface TourTranslation {
  id: string;
  tourId: string;
  languageId: string;
  voiceoverId: string | null;
  name: string;
  description: string | null;
  tagline: string | null;
  conclusion: string | null;
  createdAt: string;
  updatedAt: string;
  tour?: {
    id: string;
    name: string;
  };
  language?: {
    id: string;
    name: string;
    code: string;
  };
  voiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  conclusionVoiceover?: {
    id: string;
    fileName: string;
    name: string;
    fileType: string;
    duration?: number;
    url?: string;
  };
  // Sync status fields (computed by API)
  isNameOutdated?: boolean | null;
  isDescriptionOutdated?: boolean | null;
  isTaglineOutdated?: boolean | null;
  isConclusionOutdated?: boolean | null;
  isDescriptionVoiceoverInSync?: boolean | null;
  isConclusionVoiceoverInSync?: boolean | null;
}

/**
 * Factory function to create tour translations config for a specific tour
 */
export const createTourTranslationsConfig = (
  tourId: string,
  options?: {
    onGenerateTranslations?: () => void;
  },
): CrudConfig<TourTranslation> => ({
  entityName: 'Translation',
  entityNamePlural: 'Translations',

  endpoints: {
    list: `/tours/${tourId}/translations`,
    single: (id: string) => `/tours/${tourId}/translations/${id}`,
    // Translations are generated, not manually created (disabled via actions below)
    create: `/tours/${tourId}/translations`,
    update: (id: string) => `/tours/${tourId}/translations/${id}`,
    // Translations are generated, not manually deleted (disabled via actions below)
    delete: (id: string) => `/tours/${tourId}/translations/${id}`,
  },

  fields: [
    {
      key: 'language',
      label: 'Language',
      type: 'custom',
      required: false,
      format: (_value: TourTranslation['language'], fullRecord?: TourTranslation) => {
        if (!fullRecord?.language) return '—';
        return (
          <div>
            <div className="font-medium">{fullRecord.language.name}</div>
            <div className="text-xs text-muted-foreground">{fullRecord.language.code}</div>
          </div>
        );
      },
    },
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      format: (value: string, fullRecord?: TourTranslation) => {
        return (
          <div className="flex items-center gap-2">
            <span>{value}</span>
            <SyncWarningBadge
              isInSync={
                fullRecord?.isNameOutdated === true
                  ? false
                  : fullRecord?.isNameOutdated === false
                    ? true
                    : null
              }
              message="Source tour name has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'tagline',
      label: 'Tagline',
      type: 'text',
      required: false,
      format: (value: string | null, fullRecord?: TourTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div>{value || '-'}</div>
            <SyncWarningBadge
              isInSync={
                fullRecord?.isTaglineOutdated === true
                  ? false
                  : fullRecord?.isTaglineOutdated === false
                    ? true
                    : null
              }
              message="Source tour tagline has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: TourTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isDescriptionOutdated === true
                  ? false
                  : fullRecord?.isDescriptionOutdated === false
                    ? true
                    : null
              }
              message="Source tour description has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'conclusion',
      label: 'Conclusion',
      type: 'richtext',
      required: false,
      format: (value: string | null, fullRecord?: TourTranslation) => {
        return (
          <div className="flex flex-col gap-2">
            <div dangerouslySetInnerHTML={{ __html: value || '-' }} />
            <SyncWarningBadge
              isInSync={
                fullRecord?.isConclusionOutdated === true
                  ? false
                  : fullRecord?.isConclusionOutdated === false
                    ? true
                    : null
              }
              message="Source tour conclusion has changed since this translation was generated"
            />
          </div>
        );
      },
    },
    {
      key: 'voiceoverId',
      label: 'Description Voiceover',
      type: 'audio',
      required: false,
      readonly: true,
    },
    {
      key: 'voiceover',
      label: 'Description Voiceover',
      type: 'custom',
      required: false,
      format: (value: TourTranslation['voiceover'], fullRecord?: TourTranslation) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isDescriptionVoiceoverInSync ?? null}
            outOfSyncMessage="Description has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'conclusionVoiceover',
      label: 'Conclusion Voiceover',
      type: 'custom',
      required: false,
      format: (value: TourTranslation['conclusionVoiceover'], fullRecord?: TourTranslation) => {
        return (
          <VoiceoverAudioPlayer
            voiceover={value}
            isInSync={fullRecord?.isConclusionVoiceoverInSync ?? null}
            outOfSyncMessage="Conclusion has changed since voiceover was generated"
          />
        );
      },
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['language', 'name', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'description'],
    sortableFields: ['createdAt'],
    defaultSort: {
      field: 'createdAt',
      direction: 'desc',
    },
    itemsPerPage: 10,
    emptyState: {
      icon: <Languages className="h-12 w-12" />,
      title: 'No translations yet',
      description: 'Generate translations to make this tour available in multiple languages',
      action: options?.onGenerateTranslations
        ? {
            label: 'Generate Translations',
            icon: Languages,
            onClick: options.onGenerateTranslations,
          }
        : undefined,
    },
    actions: {
      create: {
        enabled: false, // Translations are generated, not manually created
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: false, // Translations are generated, not manually deleted
      },
    },
  },

  form: {
    updateSchema: updateTourTranslationSchema,
    sections: [
      {
        title: 'Translation Content',
        description: 'Edit translation text',
        fields: ['name', 'tagline', 'description', 'conclusion'],
        columns: 1,
      },
    ],
  },

  detailView: {
    sections: [
      {
        title: 'Translation Information',
        description: 'AI-generated translation content',
        fields: ['language', 'name', 'tagline'],
        defaultExpanded: true,
      },
      {
        title: 'Description',
        description: 'Translated description with voiceover',
        fields: ['voiceover', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Conclusion',
        description: 'Translated conclusion with voiceover',
        fields: ['conclusionVoiceover', 'conclusion'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },
});
